import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {Possessed} from "../Possessed";
import {TimelineLite, TimelineMax} from "gsap";
import {SimpleReelModelEventData, SimpleReelModelState} from "../../core/reel/simplereel/SimpleReelModel";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {PossessedReelsView} from "../reels/PossessedReelsView";
import {ArrayUtils} from "../utils/ArrayUtils";
import { PossessedScatterSymbol } from '../reels/PossessedScatterSymbol';
import {gsap} from "gsap"


/**
 * Created by Jerker Nord on 2021-01-28.
 */

export class PossessedNormalResultPresentation implements FlowStatePresentation {
    private tl:gsap.core.Timeline;
    private stoppedReels:boolean[];

    private scatterSymbols:PossessedScatterSymbol[] = [];
    private scatterLandAdditionalDelay:number = 0.7;

    private controller:FlowStateController;
    private skipped = false;

    private reelStopSounds:string[] = ["reelLanding1", "reelLanding2", "reelLanding3", "reelLanding4", "reelLanding5"];

    constructor() {

    }

    private get shouldSpinReverse():boolean {
        return Possessed.model.isFreespins();
    }

    private preventSkipOnLastReel(reelId:number, flowStateController:FlowStateController){
        const tl = gsap.timeline();
        if (reelId == 4){
            tl.add(()=>{flowStateController.setSkipCallback();}); //prevents useless skips when last reel has started its stop anyway.
        }
        return tl;
    }

    public start(flowStateController:FlowStateController) {
        this.controller = flowStateController;
        this.skipped = false;
        flowStateController.setSkipCallback(() => this.skip())
        this.stoppedReels = [false, false, false, false, false];

        ArrayUtils.shuffle(this.reelStopSounds);

        this.tl = gsap.timeline();

        const stopReversed = this.shouldSpinReverse;

        const stopDelay = Possessed.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ?  PossessedReelsView.reelStopDelayFastSpin : PossessedReelsView.reelStopDelay;
        let stopTime = 0;

        let scattersLanded = 0;
        let playingAnticipationSound:boolean = false;

        for (let i:number = 0; i < Possessed.model.gameData.reels.length; i++) {
            const stopTL = gsap.timeline();
            const reelData = Possessed.model.gameData.reels[i];

            const wildSymbolData = Possessed.model.getWildSymbol();
            const anticipation:boolean = i == 2 && (wildSymbolData ? wildSymbolData.shouldExpand : false);

            let reelStopDelay = stopDelay;
            if(i == 3) {
                stopTL.add(() => {
                    this.controller.setSkipCallback();
                })
            }
            stopTL.add(this.preventSkipOnLastReel(i,flowStateController), "<");

            const reelScatterAnticipation = scattersLanded > 1 && (!Possessed.model.data.wasFeatureBuy || Possessed.model.buyFeatureName == "LUCKY_DRAW");
            if(reelScatterAnticipation) {
                const anticipationTL = Possessed.reelView.playScatterReelAnticipation(i);
                stopTL.add(anticipationTL);
                if(scattersLanded == 2 && !playingAnticipationSound) {
                    stopTL.add(() => {
                        Possessed.soundPlayer.playSound("scatterAnticipation");
                    }, "<")
                    playingAnticipationSound = true;
                }

                reelStopDelay = anticipationTL.totalDuration();
                // Darken non active reels
                for(let j:number = 0; j < Possessed.model.gameData.reels.length; j++) {
                    if(j == i) {
                        stopTL.add(Possessed.reelView.playReelUndarken(j), "<");
                    } else {
                        stopTL.add(Possessed.reelView.playReelDarkening(j), "<");
                    }
                }
            }

            stopTL.add(Possessed.reelView.reelModels[i].stop(reelData, anticipation, stopReversed), reelScatterAnticipation ? "<1.5" : ">");
            if(anticipation) {
                stopTL.add(Possessed.reelView.playGreenAnticipationEffect(), "<");
                stopTL.add(() => {
                    Possessed.soundPlayer.playSound("wildAnticipation");
                }, "<")
            }

            const scatterLanded = reelData.filter(s => s == "S").length;
            if(scatterLanded) {
                stopTL.add(() => {
                    if(Possessed.model.isFreespins()) {
                        const sound = Possessed.model.data.freeSpinType == 3 ? "scatterLand4" : Possessed.model.data.freeSpinType == 4 ? "scatterLand5" : "scatterLandBelzebub";
                        Possessed.soundPlayer.playSound(sound);
                    } else {
                        if(this.scatterSymbols.length+1 == 2 && i == 4) {
                            // Second scatter landed on last reel
                            Possessed.soundPlayer.playSound("scatterLand1");
                        } else {
                            Possessed.soundPlayer.playSound(`scatterLand${this.scatterSymbols.length+1}`);
                        }
                    }
                }, ">")
            }

            if(reelScatterAnticipation) {

            }

            stopTL.add(()=>{});
            stopTL.add(Possessed.reelView.reelModels[i].getAllVisibleStopSymbols().map(s => s.getLandAnimation()), "<")
            stopTL.add(() => {});

            if(i == 2 && Possessed.model.getWildSymbol()?.shouldExpand) {
                stopTL.add(Possessed.reelView.stopGreenAnticipationEffect());
                stopTL.add(Possessed.reelView.playWildAnticipationEffects(), "<");
            }

            Possessed.reelView.reelModels[i].events?.once(SimpleReelModelState.STOPPED, (data:SimpleReelModelEventData)=>this.onReelStop(data, flowStateController));
            Possessed.reelView.reelModels[i].events?.once(SimpleReelModelState.BOUNCE, (data:SimpleReelModelEventData)=>this.onReelBounce(data));

            scattersLanded += reelData.filter(s => s == "S").length;

            this.tl.add(stopTL, stopTime);
            if(anticipation) {
                stopTime += stopTL.totalDuration();
            } else {
                stopTime += reelStopDelay;
            }
        }

        const padding = Possessed.gameFlowController.calculatePaddingDuration(this.tl.totalDuration());
        this.tl.add(() => {flowStateController.finish()}, ">0.2");
        if (padding > 0) {
            //Logger.logDev("Added minimum jurisdiction padding  ", padding);
            //Logger.logDev("Total before padding:"+this._tl.totalDuration());
            this.tl.shiftChildren((padding + 0.1));
            //Logger.logDev("Total after padding:"+this._tl.totalDuration());
        }
    }

    private skip():void {
        this.tl?.kill();
        this.tl = gsap.timeline();
        const stopDelay = 0;
        let stopTime = 0;
        this.tl.add(()=> {
            Possessed.reelView.hideActivatorLandAnim();
            Possessed.reelView.removeAllReelDarkening();
            Possessed.reelView.stopGreenAnticipationEffect();
            Possessed.soundPlayer.stopSound("scatterAnticipation");
            Possessed.soundPlayer.stopSound("wildAnticipation", 30);
            this.skipped = true;
        })
        for (let i:number = 0; i < Possessed.model.gameData.reels.length; i++) {
            const reelModel = Possessed.reelView.reelModels[i];
            if (reelModel.state != SimpleReelModelState.SPIN) {
                continue;
            }

            const reelData = Possessed.model.gameData.reels[i];
            const reelStopAnimation = gsap.timeline();
            reelStopAnimation.add(Possessed.reelView.reelModels[i].stop(reelData, false, this.shouldSpinReverse));

            this.tl.add(reelStopAnimation, stopTime);
            stopTime += stopDelay;
        }
    }

    private onReelBounce(data:SimpleReelModelEventData) {
        //Play sound maybe?
    }

    private onReelStop(data:SimpleReelModelEventData, flowStateController:FlowStateController) {
        this.stoppedReels[data.reel] = true;
        Possessed.soundPlayer.playSoundWithDavidPanning(this.reelStopSounds[data.reel], data.reel);

        if(this.scatterSymbols.length > 1) {
            if(data.reel != 4) {
                const randomStopSoundIndex = Math.floor(Math.random() * 2) + 1;
                const randomStopSound = `scatterAnticipationLand0${randomStopSoundIndex}`;
                Possessed.soundPlayer.playSoundWithDavidPanning(randomStopSound, data.reel);
            } else {
                Possessed.soundPlayer.stopSound("scatterAnticipation", 50);
                Possessed.soundPlayer.playSound("scatterAnticipationEnd");
            }
        }

        // Scatter anticipation
        const landedScatter = <PossessedScatterSymbol|undefined>Possessed.reelView.reelModels[data.reel].getAllVisibleSymbols().find(s => s.name == "S");
        if(landedScatter && !(this.scatterSymbols.length == 1 && data.reel == 4)) {
            //landedScatter.moveToScatterLayer();
            this.scatterSymbols.push(landedScatter);
            this.scatterSymbols.forEach(s => s.updateScatterAnimation(this.scatterSymbols.length));
        }

        if (ArrayUtils.isAllTrue(this.stoppedReels)){
            Possessed.soundPlayer.stopSound("reelsSpinning");

            const completedTL = gsap.timeline();

            this.stoppedReels.forEach((_, index) => {
                completedTL.add(Possessed.reelView.playReelUndarken(index, 0.2), "<")
            })
            if(this.skipped) {
                // https://github.com/nolimitcity/possessed/issues/217
                const scatters = <PossessedScatterSymbol[]>Possessed.reelView.getAllVisibleSymbols().filter(s => s.name == "S");
                completedTL.add(() => {
                    scatters.forEach(s => s.updateScatterAnimation(scatters.length));
                })
                // ---------------------------------------------------
                completedTL.add(() => {
                    this.controller.finish();
                })
                completedTL.add(() => {});
            }

            this.scatterSymbols.forEach(s => s.moveToReelLayer());
            if(this.scatterSymbols.length == 2) {
                this.scatterSymbols.forEach(sym => sym.resetState());
            }

            this.scatterSymbols = [];
        }
    }

    //private completePresentation(flowStateController:FlowStateController) {
    //    flowStateController.finish();
    //    //delete this._tl;
    //}
}